import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FaHome, FaCamera, FaChartBar, FaCog, FaPlus, 
  FaExpand, FaCompress, FaClock, FaCalendar, FaCreditCard,
  FaExclamationCircle, FiCheckCircle, FiInfo, FaTimes, FaSearch,
  FaMapPin, FaUser, FaEnvelope, FiPhone, FaCar, FaTruck, FaDownload
} from 'react-icons/fa';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './App.css';

// Register Chart.js components
Chart.register(...registerables);

// Constants
const COOLDOWN_MINUTES = 2;
const COOLDOWN_MS = COOLDOWN_MINUTES * 60 * 1000;
const API_BASE_URL = 'https://parksmart-rt5c.vercel.app/api';
const VEHICLE_TYPES = ['Car', 'SUV', 'Truck', 'Motorcycle'];
const PAYMENT_METHODS = ['Credit Card', 'Mobile Payment', 'Cash', 'Subscription'];

function App() {
  // State management
  const [slots, setSlots] = useState([]);
  const [status, setStatus] = useState('Waiting for vehicle...');
  const [processing, setProcessing] = useState(false);
  const [detectionStatus, setDetectionStatus] = useState('idle');
  const [analytics, setAnalytics] = useState(null);
  const [reserveSlot, setReserveSlot] = useState('');
  const [reserveEmail, setReserveEmail] = useState('');
  const [reservePhone, setReservePhone] = useState('');
  const [reserveDate, setReserveDate] = useState(new Date());
  const [reserveDuration, setReserveDuration] = useState(1);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [manualPlateInput, setManualPlateInput] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [vehicleType, setVehicleType] = useState('Car');
  const [paymentMethod, setPaymentMethod] = useState('Credit Card');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCVC, setCardCVC] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSlots, setFilteredSlots] = useState([]);
  const [occupancyRate, setOccupancyRate] = useState(0);
  const [revenueData, setRevenueData] = useState({});
  const [occupancyHistory, setOccupancyHistory] = useState({});
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrData, setQRData] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [slotView, setSlotView] = useState('grid'); // 'grid' or 'map'
  const [userProfile, setUserProfile] = useState({
    name: 'Guest User',
    email: '',
    phone: '',
    licensePlate: '',
    subscriptionType: 'None',
    paymentMethods: []
  });
  
  // Refs
  const videoRef = useRef(null);
  const motionDetectionInterval = useRef(null);
  const plateHistory = useRef(new Map());
  const canvasRef = useRef(null);
  const receiptRef = useRef(null);
  const mapRef = useRef(null);

  // Filter slots based on search query
  useEffect(() => {
    if (!slots.length) return;
    
    const filtered = slots.filter(slot => {
      const query = searchQuery.toLowerCase();
      return (
        slot.slotNumber.toLowerCase().includes(query) ||
        (slot.vehicleNumber && slot.vehicleNumber.toLowerCase().includes(query)) ||
        (slot.reservedBy && slot.reservedBy.toLowerCase().includes(query))
      );
    });
    
    setFilteredSlots(filtered);
  }, [searchQuery, slots]);

  // Calculate occupancy rate
  useEffect(() => {
    if (!slots.length) return;
    
    const occupied = slots.filter(slot => slot.isOccupied).length;
    const rate = (occupied / slots.length) * 100;
    setOccupancyRate(Math.round(rate));
  }, [slots]);

  // Fetch parking data
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [slotsResponse, analyticsResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/slots`),
          axios.get(`${API_BASE_URL}/analytics`)
        ]);
        setSlots(slotsResponse.data.slots);
        setAnalytics(analyticsResponse.data);
        
        // Generate mock data for charts if analytics doesn't provide it
        generateMockChartData();
        
        setError(null);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load parking data. Please try again.');
        
        // Generate mock data for development
        generateMockData();
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000); // Reduced polling frequency

    // Cleanup old plate history
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      plateHistory.current.forEach((timestamp, plate) => {
        if (now - timestamp > COOLDOWN_MS * 2) {
          plateHistory.current.delete(plate);
        }
      });
    }, 60000);

    return () => {
      clearInterval(interval);
      clearInterval(cleanupInterval);
    };
  }, []);

  // Generate mock data for development
  const generateMockData = () => {
    // Generate mock slots
    const mockSlots = Array.from({ length: 30 }, (_, i) => {
      const slotNumber = `A${(i + 1).toString().padStart(2, '0')}`;
      const isOccupied = Math.random() > 0.6;
      const isReserved = !isOccupied && Math.random() > 0.8;
      
      return {
        slotNumber,
        isOccupied,
        isReserved,
        vehicleNumber: isOccupied ? `ABC${Math.floor(Math.random() * 1000)}` : null,
        entryTime: isOccupied ? new Date(Date.now() - Math.random() * 3600000 * 5) : null,
        reservedBy: isReserved ? 'Reserved User' : null,
        reservationExpiry: isReserved ? new Date(Date.now() + Math.random() * 3600000 * 24) : null,
        vehicleType: isOccupied ? VEHICLE_TYPES[Math.floor(Math.random() * VEHICLE_TYPES.length)] : null
      };
    });
    
    setSlots(mockSlots);
    
    // Generate mock analytics
    setAnalytics({
      totalSpots: 30,
      occupied: mockSlots.filter(s => s.isOccupied).length,
      reserved: mockSlots.filter(s => s.isReserved).length,
      available: mockSlots.filter(s => !s.isOccupied && !s.isReserved).length,
      averageDuration: 2.5,
      peakHours: [12, 17, 18],
      revenue: {
        daily: 450,
        weekly: 2800,
        monthly: 12000
      }
    });
    
    generateMockChartData();
  };
  
  // Generate mock chart data
  const generateMockChartData = () => {
    // Revenue data for the past week
    const revenueLabels = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    });
    
    const revenueValues = Array.from({ length: 7 }, () => 
      Math.floor(Math.random() * 500) + 300
    );
    
    setRevenueData({
      labels: revenueLabels,
      datasets: [{
        label: 'Daily Revenue ($)',
        data: revenueValues,
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
        tension: 0.4
      }]
    });
    
    // Occupancy history for the past 24 hours
    const hourLabels = Array.from({ length: 24 }, (_, i) => `${i}:00`);
    const occupancyValues = Array.from({ length: 24 }, () => 
      Math.floor(Math.random() * 100)
    );
    
    setOccupancyHistory({
      labels: hourLabels,
      datasets: [{
        label: 'Occupancy Rate (%)',
        data: occupancyValues,
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 2,
        tension: 0.4
      }]
    });
  };

  // Initialize camera
  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment', 
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          } 
        });
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          startMotionDetection();
        }
      } catch (error) {
        setStatus('Camera access denied - Please enable permissions');
        addNotification('error', 'Camera Error', 'Unable to access camera. Please check permissions.');
        console.error('Camera error:', error);
      }
    };

    if (activeTab === 'camera') {
      startCamera();
    }

    return () => {
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
      if (motionDetectionInterval.current) {
        clearInterval(motionDetectionInterval.current);
      }
    };
  }, [activeTab]);

  // Initialize canvas for image processing
  useEffect(() => {
    if (activeTab === 'camera' && videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = 1920;
      canvas.height = 1080;
      canvasRef.current = canvas;
    }
  }, [activeTab]);

  // Apply dark mode
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // Motion detection setup
  const startMotionDetection = () => {
    let lastFrame = null;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    motionDetectionInterval.current = setInterval(async () => {
      if (!videoRef.current || processing || activeTab !== 'camera') return;

      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const currentFrame = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      
      if (lastFrame && detectMotion(lastFrame, currentFrame)) {
        handleVehicleDetection(canvas);
      }
      lastFrame = currentFrame;
    }, 1500);
  };

  // Motion detection algorithm
  const detectMotion = (prevFrame, currentFrame) => {
    let diff = 0;
    const skipPixels = 4; // Process every 4th pixel for performance
    for (let i = 0; i < currentFrame.length; i += 4 * skipPixels) {
      diff += Math.abs(prevFrame[i] - currentFrame[i]);
    }
    return diff / (currentFrame.length / (4 * skipPixels)) > 20; // Increased threshold for better detection
  };

  // Handle vehicle detection
  const handleVehicleDetection = async (canvas) => {
    setProcessing(true);
    setDetectionStatus('detecting');
    try {
      // Apply image preprocessing for better plate detection
      applyImagePreprocessing(canvas);
      
      // Detect vehicle type using AI (mock implementation)
      const detectedVehicleType = detectVehicleType(canvas);
      setVehicleType(detectedVehicleType);
      
      const plate = await processLicensePlate(canvas);
      if (!plate) {
        setStatus('No license plate detected. Try adjusting camera position.');
        setDetectionStatus('error');
        return;
      }

      const { data: currentSlots } = await axios.get(`${API_BASE_URL}/slots`);
      const isParked = currentSlots.slots.some(slot => slot.vehicleNumber === plate && slot.isOccupied);
      const isReserved = currentSlots.slots.some(slot => 
        slot.vehicleNumber === plate && slot.isReserved && !slot.isOccupied
      );

      setDetectionStatus('success');
      
      if (isParked) {
        await handleVehicleExit(plate);
      } else if (isReserved) {
        await handleReservedVehicleEntry(plate);
      } else {
        const now = Date.now();
        const lastDetected = plateHistory.current.get(plate);
        if (lastDetected && now - lastDetected < COOLDOWN_MS) {
          setStatus(`Recent entry - ignoring: ${plate}`);
          return;
        }
        await handleVehicleEntry(plate, detectedVehicleType);
      }

      const { data } = await axios.get(`${API_BASE_URL}/slots`);
      setSlots(data.slots);

    } catch (error) {
      setDetectionStatus('error');
      setStatus(error.message || 'Detection failed');
      addNotification('error', 'Detection Error', error.message || 'Failed to detect license plate');
    } finally {
      setTimeout(() => {
        setProcessing(false);
        setDetectionStatus('idle');
      }, 2000);
    }
  };

  // Mock vehicle type detection
  const detectVehicleType = (canvas) => {
    // In a real implementation, this would use AI to analyze the vehicle image
    // For now, we'll return a random vehicle type
    return VEHICLE_TYPES[Math.floor(Math.random() * VEHICLE_TYPES.length)];
  };

  // Apply image preprocessing for better license plate detection
  const applyImagePreprocessing = (canvas) => {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Apply contrast enhancement
    const factor = 1.5; // Contrast factor
    const intercept = 128 * (1 - factor);
    
    for (let i = 0; i < data.length; i += 4) {
      // Apply contrast to RGB channels
      data[i] = factor * data[i] + intercept;     // R
      data[i + 1] = factor * data[i + 1] + intercept; // G
      data[i + 2] = factor * data[i + 2] + intercept; // B
    }
    
    ctx.putImageData(imageData, 0, 0);
    
    // Apply digital zoom if needed
    if (zoomLevel > 1) {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const scaledWidth = canvas.width / zoomLevel;
      const scaledHeight = canvas.height / zoomLevel;
      
      // Save the enhanced image
      const enhancedImage = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw zoomed portion
      ctx.drawImage(
        canvas, 
        centerX - scaledWidth / 2, 
        centerY - scaledHeight / 2, 
        scaledWidth, 
        scaledHeight, 
        0, 
        0, 
        canvas.width, 
        canvas.height
      );
    }
  };

  // Process license plate from image
  const processLicensePlate = async (canvas) => {
    setStatus('Processing license plate...');
    try {
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9)); // Higher quality
      const formData = new FormData();
      formData.append('image', blob, 'plate.jpg');

      const response = await axios.post(
        `${API_BASE_URL}/detect`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (!response.data.plate) throw new Error('No plate detected');
      return response.data.plate;
    } catch (error) {
      throw new Error('License plate not recognized. Try manual entry.');
    }
  };

  // Handle vehicle entry
  const handleVehicleEntry = async (plate, vehicleType) => {
    try {
      // Find best slot based on vehicle type and availability
      const response = await axios.post(`${API_BASE_URL}/entry-auto`, { 
        plate,
        vehicleType,
        entryTime: new Date()
      });
      
      setStatus(`Vehicle ${plate} parked successfully in slot ${response.data.slotNumber}`);
      addNotification('success', 'Vehicle Entry', `Vehicle ${plate} parked in slot ${response.data.slotNumber}`);
      plateHistory.current.set(plate, Date.now() + COOLDOWN_MS);
      
      // Generate QR code for this parking session
      generateParkingQR(response.data);
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Entry failed');
    }
  };

  // Handle reserved vehicle entry
  const handleReservedVehicleEntry = async (plate) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/reserved-entry`, { plate });
      
      setStatus(`Reserved vehicle ${plate} parked in slot ${response.data.slotNumber}`);
      addNotification('success', 'Reserved Vehicle Entry', `Vehicle ${plate} parked in reserved slot ${response.data.slotNumber}`);
      plateHistory.current.set(plate, Date.now() + COOLDOWN_MS);
      
      // Generate QR code for this parking session
      generateParkingQR(response.data);
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Reserved entry failed');
    }
  };

  // Handle vehicle exit
  const handleVehicleExit = async (plate) => {
    try {
      // Show payment modal
      const slotInfo = slots.find(slot => slot.vehicleNumber === plate && slot.isOccupied);
      if (slotInfo) {
        const entryTime = new Date(slotInfo.entryTime);
        const exitTime = new Date();
        const durationHours = Math.ceil((exitTime - entryTime) / 3600000);
        const fee = durationHours * 5; // $5 per hour
        
        setPaymentAmount(fee);
        setShowPaymentModal(true);
        
        // For demo purposes, we'll automatically process the payment after 3 seconds
        setTimeout(() => {
          processPayment(plate, fee);
        }, 3000);
      }
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Exit failed');
    }
  };

  // Process payment and complete exit
  const processPayment = async (plate, fee) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auto-exit`, { 
        plate,
        paymentMethod,
        paymentAmount: fee
      });
      
      setShowPaymentModal(false);
      
      if (response.data.success) {
        setStatus(`Vehicle ${plate} exited - Fee: $${fee}`);
        addNotification('info', 'Vehicle Exit', `Vehicle ${plate} exited. Fee: $${fee}`);
        plateHistory.current.delete(plate);
        
        // Generate receipt
        setReceiptData({
          vehicleNumber: plate,
          entryTime: new Date(response.data.entryTime || Date.now() - (response.data.duration * 3600000)),
          exitTime: new Date(response.data.exitTime),
          fee: fee,
          duration: response.data.duration,
          slotNumber: slots.find(slot => slot.vehicleNumber === plate)?.slotNumber || 'Unknown',
          paymentMethod: paymentMethod
        });
        setShowReceipt(true);
      }
    } catch (error) {
      setShowPaymentModal(false);
      throw new Error(error.response?.data?.error || 'Payment failed');
    }
  };

  // Generate QR code for parking session
  const generateParkingQR = (parkingData) => {
    const qrData = {
      type: 'parking_session',
      vehicleNumber: parkingData.vehicleNumber,
      slotNumber: parkingData.slotNumber,
      entryTime: parkingData.entryTime,
      receiptId: parkingData.receiptId
    };
    
    setQRData(qrData);
    setShowQRModal(true);
  };

  // Handle manual vehicle removal
  const handleManualRemove = async (plate) => {
    try {
      const confirmRemove = window.confirm(`Force remove vehicle ${plate}?`);
      if (!confirmRemove) return;

      const response = await axios.delete(
        `${API_BASE_URL}/manual-remove/${encodeURIComponent(plate)}`
      );

      if (response.data.success) {
        setSlots(prev => prev.map(slot => 
          slot.vehicleNumber === plate ? { 
            ...slot, 
            isOccupied: false,
            vehicleNumber: null,
            entryTime: null
          } : slot
        ));
        setStatus(`Vehicle ${plate} manually removed`);
        addNotification('info', 'Manual Removal', `Vehicle ${plate} manually removed. Fee: $${response.data.fee}`);
        
        // Generate receipt for manual removal
        setReceiptData({
          vehicleNumber: plate,
          entryTime: new Date(Date.now() - (response.data.fee / 5) * 3600000), // Estimate based on fee
          exitTime: new Date(response.data.exitTime),
          fee: response.data.fee,
          duration: Math.ceil(response.data.fee / 5),
          slotNumber: slots.find(slot => slot.vehicleNumber === plate)?.slotNumber || 'Unknown',
          isManualRemoval: true,
          paymentMethod: 'Manual'
        });
        setShowReceipt(true);
      }
    } catch (error) {
      setStatus(error.response?.data?.error || 'Removal failed');
      addNotification('error', 'Removal Error', error.response?.data?.error || 'Failed to remove vehicle');
      const { data } = await axios.get(`${API_BASE_URL}/slots`);
      setSlots(data.slots);
    }
  };

  // Handle slot reservation
  const handleReserveSlot = async () => {
    try {
      if (!reserveSlot || !reserveEmail) {
        addNotification('error', 'Reservation Error', 'Please select a slot and enter an email address');
        return;
      }
      
      const response = await axios.post(`${API_BASE_URL}/reserve-slot`, {
        slotNumber: reserveSlot,
        email: reserveEmail,
        phone: reservePhone,
        reservationDate: reserveDate,
        duration: reserveDuration
      });
      
      setStatus(`Slot ${reserveSlot} reserved for ${reserveEmail}`);
      addNotification('success', 'Slot Reserved', `Slot ${reserveSlot} reserved for ${reserveEmail}`);
      
      // Generate QR code for reservation
      const qrData = {
        type: 'reservation',
        slotNumber: reserveSlot,
        email: reserveEmail,
        reservationDate: reserveDate,
        duration: reserveDuration,
        reservationId: response.data.reservationId
      };
      
      setQRData(qrData);
      setShowQRModal(true);
      
      // Reset form
      setReserveSlot('');
      setReserveEmail('');
      setReservePhone('');
      setReserveDuration(1);
      setShowReservationModal(false);
      
      // Refresh slots
      const { data } = await axios.get(`${API_BASE_URL}/slots`);
      setSlots(data.slots);
    } catch (error) {
      setStatus(error.response?.data?.error || 'Reservation failed');
      addNotification('error', 'Reservation Error', error.response?.data?.error || 'Failed to reserve slot');
    }
  };

  // Handle manual plate entry
  const handleManualPlateEntry = async () => {
    if (!manualPlateInput.trim()) {
      addNotification('error', 'Input Error', 'Please enter a license plate number');
      return;
    }
    
    const plate = manualPlateInput.toUpperCase().trim();
    setManualPlateInput('');
    
    try {
      const { data: currentSlots } = await axios.get(`${API_BASE_URL}/slots`);
      const isParked = currentSlots.slots.some(slot => slot.vehicleNumber === plate && slot.isOccupied);
      const isReserved = currentSlots.slots.some(slot => 
        slot.vehicleNumber === plate && slot.isReserved && !slot.isOccupied
      );

      if (isParked) {
        await handleVehicleExit(plate);
      } else if (isReserved) {
        await handleReservedVehicleEntry(plate);
      } else {
        await handleVehicleEntry(plate, vehicleType);
      }

      const { data } = await axios.get(`${API_BASE_URL}/slots`);
      setSlots(data.slots);
    } catch (error) {
      setStatus(error.message || 'Operation failed');
      addNotification('error', 'Operation Error', error.message || 'Failed to process vehicle');
    }
  };

  // Toggle fullscreen camera mode
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Add notification
  const addNotification = (type, title, message) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, title, message, timestamp: new Date() }]);
    
    // Show toast notification
    switch (type) {
      case 'success':
        toast.success(message);
        break;
      case 'error':
        toast.error(message);
        break;
      case 'info':
        toast.info(message);
        break;
      default:
        toast(message);
    }
    
    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, 5000);
  };

  // Print receipt
  const printReceipt = () => {
    if (receiptRef.current) {
      const printContent = receiptRef.current.innerHTML;
      const printWindow = window.open('', '_blank');
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Parking Receipt</title>
            <style>
              body { font-family: 'Poppins', sans-serif; margin: 0; padding: 20px; }
              .receipt { max-width: 400px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; }
              .receipt-header { text-align: center; margin-bottom: 20px; }
              .receipt-logo { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
              .receipt-title { font-size: 18px; margin-bottom: 15px; }
              .receipt-details { margin-bottom: 20px; }
              .receipt-row { display: flex; justify-content: space-between; margin-bottom: 8px; }
              .receipt-label { font-weight: bold; }
              .receipt-footer { text-align: center; margin-top: 20px; font-size: 14px; }
              .receipt-qr { text-align: center; margin: 15px 0; }
              .receipt-qr img { width: 100px; height: 100px; }
              @media print {
                body { margin: 0; padding: 0; }
                .receipt { border: none; }
                .no-print { display: none; }
              }
            </style>
          </head>
          <body>
            ${printContent}
            <div class="no-print" style="text-align: center; margin-top: 20px;">
              <button onclick="window.print()">Print Receipt</button>
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    }
  };

  // Email receipt
  const emailReceipt = async () => {
    try {
      // This would connect to a backend endpoint to send email
      const response = await axios.post(`${API_BASE_URL}/email-receipt`, {
        email: userProfile.email || prompt('Please enter your email:'),
        receipt: receiptData
      });
      
      if (response.data.success) {
        addNotification('success', 'Receipt Sent', 'Receipt has been emailed successfully');
      } else {
        throw new Error(response.data.error);
      }
      
      setShowReceipt(false);
    } catch (error) {
      addNotification('error', 'Email Error', 'Failed to send receipt email');
    }
  };

  // Open slot reservation modal
  const openReservationModal = (slot) => {
    setSelectedSlot(slot);
    setReserveSlot(slot.slotNumber);
    setShowReservationModal(true);
  };

  // Get slot status class
  const getSlotStatusClass = useCallback((slot) => {
    if (slot.isOccupied) return 'occupied';
    if (slot.isReserved) return 'reserved';
    return 'available';
  }, []);

  // Format date and time
  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  // Format duration
  const formatDuration = (entryTime, exitTime) => {
    if (!entryTime || !exitTime) return 'N/A';
    const durationMs = new Date(exitTime) - new Date(entryTime);
    const hours = Math.floor(durationMs / 3600000);
    const minutes = Math.floor((durationMs % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  };

  // Calculate time remaining for reservation
  const calculateTimeRemaining = (expiryDate) => {
    if (!expiryDate) return 'N/A';
    
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffMs = expiry - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const diffHours = Math.floor(diffMs / 3600000);
    const diffMinutes = Math.floor((diffMs % 3600000) / 60000);
    
    return `${diffHours}h ${diffMinutes}m`;
  };

  // Render dashboard
  const renderDashboard = () => (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2>Parking Dashboard</h2>
        <div className="analytics-summary">
          <div className="analytic-item">
            <FaCar size={24} />
            <div>
              <div className="label">Occupancy</div>
              <div className="value">{occupancyRate}%</div>
            </div>
          </div>
          <div className="analytic-item">
            <FaMapPin size={24} />
            <div>
              <div className="label">Available</div>
              <div className="value">{slots.filter(s => !s.isOccupied && !s.isReserved).length}</div>
            </div>
          </div>
          <div className="analytic-item">
            <FaClock size={24} />
            <div>
              <div className="label">Reserved</div>
              <div className="value">{slots.filter(s => s.isReserved).length}</div>
            </div>
          </div>
          <div className="analytic-item">
            <FaCreditCard size={24} />
            <div>
              <div className="label">Revenue</div>
              <div className="value">${analytics?.revenue?.daily || 0}</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="dashboard-actions">
        <div className="action-card">
          <h3>Parking Status</h3>
          <div className="chart-container">
            {occupancyHistory.labels && (
              <Line 
                data={occupancyHistory}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: true,
                      text: '24-Hour Occupancy Trend'
                    }
                  }
                }}
              />
            )}
          </div>
        </div>
        
        <div className="action-card">
          <h3>Revenue Trend</h3>
          <div className="chart-container">
            {revenueData.labels && (
              <Bar 
                data={revenueData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: true,
                      text: 'Weekly Revenue'
                    }
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>
      
      <div className="parking-section">
        <div className="section-header">
          <h3>Parking Slots</h3>
          <div className="section-actions">
            <div className="search-container">
              <FaSearch size={18} />
              <input
                type="text"
                placeholder="Search slots..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="view-toggle">
              <button 
                className={`view-btn ${slotView === 'grid' ? 'active' : ''}`}
                onClick={() => setSlotView('grid')}
              >
                Grid
              </button>
              <button 
                className={`view-btn ${slotView === 'map' ? 'active' : ''}`}
                onClick={() => setSlotView('map')}
              >
                Map
              </button>
            </div>
          </div>
        </div>
        
        {slotView === 'grid' ? (
          <div className="parking-grid">
            {(searchQuery ? filteredSlots : slots).map(slot => (
              <div 
                key={slot.slotNumber} 
                className={`parking-slot ${getSlotStatusClass(slot)}`}
                onClick={() => {
                  if (!slot.isOccupied && !slot.isReserved) {
                    openReservationModal(slot);
                  }
                }}
              >
                <div className="slot-number">{slot.slotNumber}</div>
                
                {slot.isOccupied && (
                  <div className="vehicle-info">
                    <div className="plate">{slot.vehicleNumber}</div>
                    <div className="time">Since: {new Date(slot.entryTime).toLocaleTimeString()}</div>
                    <div className="vehicle-type">{slot.vehicleType || 'Car'}</div>
                    <button 
                      className="remove-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleManualRemove(slot.vehicleNumber);
                      }}
                    >
                      <FaTimes size={16} />
                    </button>
                  </div>
                )}
                
                {slot.isReserved && !slot.isOccupied && (
                  <div className="reserved-info">
                    <div className="reserved-by">{slot.reservedBy}</div>
                    <div className="reserved-email">{slot.reservedEmail}</div>
                    <div className="reservation-time">
                      Expires in: {calculateTimeRemaining(slot.reservationExpiry)}
                    </div>
                  </div>
                )}
                
                {!slot.isOccupied && !slot.isReserved && (
                  <div className="available-info">
                    <div className="status">Available</div>
                    <div className="action-hint">Click to reserve</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="parking-map" ref={mapRef}>
            {/* This would be replaced with an actual interactive map */}
            <div className="map-placeholder">
              <div className="map-grid">
                {(searchQuery ? filteredSlots : slots).map(slot => (
                  <div 
                    key={slot.slotNumber} 
                    className={`map-slot ${getSlotStatusClass(slot)}`}
                    onClick={() => {
                      if (!slot.isOccupied && !slot.isReserved) {
                        openReservationModal(slot);
                      }
                    }}
                  >
                    <div className="map-slot-number">{slot.slotNumber}</div>
                    {slot.isOccupied && <FaCar size={16} />}
                    {slot.isReserved && !slot.isOccupied && <FaClock size={16} />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="action-card">
        <h3>Quick Actions</h3>
        <div className="quick-actions">
          <button 
            className="action-button primary"
            onClick={() => setActiveTab('camera')}
          >
            <FaCamera size={20} />
            Scan Vehicle
          </button>
          
          <button 
            className="action-button secondary"
            onClick={() => setShowReservationModal(true)}
          >
            <a size={20} />
            New Reservation
          </button>
          
          <div className="manual-entry">
            <h4>Manual Entry</h4>
            <div className="manual-entry-form">
              <input
                type="text"
                placeholder="Enter license plate"
                value={manualPlateInput}
                onChange={(e) => setManualPlateInput(e.target.value.toUpperCase())}
                className="form-input"
              />
              <select 
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                className="form-select"
              >
                {VEHICLE_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <button 
                className="form-button"
                onClick={handleManualPlateEntry}
                disabled={!manualPlateInput.trim()}
              >
                Process Vehicle
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render camera view
  const renderCamera = () => (
    <div className={`camera-container ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className={`camera-feed ${detectionStatus}`}>
        <video ref={videoRef} autoPlay playsInline muted />
        
        <div className="status-overlay">
          <div className="status-text">{status}</div>
          <div className="detection-indicator">
            {detectionStatus === 'idle' && 'Ready'}
            {detectionStatus === 'detecting' && 'Detecting...'}
            {detectionStatus === 'success' && 'Detected!'}
            {detectionStatus === 'error' && 'Error!'}
          </div>
        </div>
        
        <div className={`camera-controls ${isFullscreen ? 'fullscreen-controls' : ''}`}>
          <div className="zoom-controls">
            <button 
              className="zoom-btn"
              onClick={() => setZoomLevel(prev => Math.max(1, prev - 0.5))}
              disabled={zoomLevel <= 1}
            >
              <FaCompress />
            </button>
            <span className="zoom-level">{zoomLevel}x</span>
            <button 
              className="zoom-btn"
              onClick={() => setZoomLevel(prev => Math.min(4, prev + 0.5))}
              disabled={zoomLevel >= 4}
            >
              <a />
            </button>
          </div>
          
          <button 
            className="fullscreen-btn"
            onClick={toggleFullscreen}
          >
            {isFullscreen ? <FaCompress /> : <FaExpand />}
            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </button>
        </div>
      </div>
      
      <div className="camera-info">
        <h3>License Plate Detection</h3>
        <p>
          Position the vehicle license plate in the camera view. The system will automatically
          detect vehicle presence and process the license plate.
        </p>
        
        <div className="detection-status">
          <div className="status-label">Status:</div>
          <div className={`status-value ${detectionStatus}`}>
            {detectionStatus === 'idle' && 'Ready'}
            {detectionStatus === 'detecting' && 'Detecting...'}
            {detectionStatus === 'success' && 'Detected!'}
            {detectionStatus === 'error' && 'Error!'}
          </div>
        </div>
        
        <div className="manual-entry">
          <h4>Manual Entry</h4>
          <div className="manual-entry-form">
            <input
              type="text"
              placeholder="Enter license plate"
              value={manualPlateInput}
              onChange={(e) => setManualPlateInput(e.target.value.toUpperCase())}
              className="form-input"
            />
            <select 
              value={vehicleType}
              onChange={(e) => setVehicleType(e.target.value)}
              className="form-select"
            >
              {VEHICLE_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <button 
              className="form-button"
              onClick={handleManualPlateEntry}
              disabled={!manualPlateInput.trim()}
            >
              Process Vehicle
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render analytics view
  const renderAnalytics = () => (
    <div className="analytics-container">
      <div className="dashboard-header">
        <h2>Parking Analytics</h2>
      </div>
      
      <div className="analytics-grid">
        <div className="analytics-card">
          <h3>Occupancy by Time</h3>
          <div className="chart-container">
            {occupancyHistory.labels && (
              <Line 
                data={occupancyHistory}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: true,
                      text: '24-Hour Occupancy Trend'
                    }
                  }
                }}
              />
            )}
          </div>
        </div>
        
        <div className="analytics-card">
          <h3>Revenue Analysis</h3>
          <div className="chart-container">
            {revenueData.labels && (
              <Bar 
                data={revenueData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: true,
                      text: 'Weekly Revenue'
                    }
                  }
                }}
              />
            )}
          </div>
        </div>
        
        <div className="analytics-card">
          <h3>Vehicle Types</h3>
          <div className="chart-container">
            <Pie 
              data={{
                labels: VEHICLE_TYPES,
                datasets: [{
                  data: [40, 30, 20, 10], // Mock data
                  backgroundColor: [
                    'rgba(59, 130, 246, 0.7)',
                    'rgba(16, 185, 129, 0.7)',
                    'rgba(245, 158, 11, 0.7)',
                    'rgba(239, 68, 68, 0.7)'
                  ],
                  borderColor: [
                    'rgba(59, 130, 246, 1)',
                    'rgba(16, 185, 129, 1)',
                    'rgba(245, 158, 11, 1)',
                    'rgba(239, 68, 68, 1)'
                  ],
                  borderWidth: 1
                }]
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'right',
                  },
                  title: {
                    display: true,
                    text: 'Vehicle Distribution'
                  }
                }
              }}
            />
          </div>
        </div>
        
        <div className="analytics-card">
          <h3>Payment Methods</h3>
          <div className="chart-container">
            <Pie 
              data={{
                labels: PAYMENT_METHODS,
                datasets: [{
                  data: [45, 25, 20, 10], // Mock data
                  backgroundColor: [
                    'rgba(59, 130, 246, 0.7)',
                    'rgba(16, 185, 129, 0.7)',
                    'rgba(245, 158, 11, 0.7)',
                    'rgba(239, 68, 68, 0.7)'
                  ],
                  borderColor: [
                    'rgba(59, 130, 246, 1)',
                    'rgba(16, 185, 129, 1)',
                    'rgba(245, 158, 11, 1)',
                    'rgba(239, 68, 68, 1)'
                  ],
                  borderWidth: 1
                }]
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'right',
                  },
                  title: {
                    display: true,
                    text: 'Payment Distribution'
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
      
      <div className="analytics-card">
        <h3>Parking Duration Analysis</h3>
        <div className="chart-container">
          <Bar 
            data={{
              labels: ['<1h', '1-2h', '2-4h', '4-8h', '>8h'],
              datasets: [{
                label: 'Number of Vehicles',
                data: [15, 25, 30, 20, 10], // Mock data
                backgroundColor: 'rgba(59, 130, 246, 0.7)',
                borderColor: 'rgba(59, 130, 246, 1)',
                borderWidth: 1
              }]
            }}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                title: {
                  display: true,
                  text: 'Parking Duration Distribution'
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );

  // Render settings view
  const renderSettings = () => (
    <div className="settings-container">
      <div className="dashboard-header">
        <h2>System Settings</h2>
      </div>
      
      <div className="settings-grid">
        <div className="settings-card">
          <h3>User Profile</h3>
          <div className="settings-form">
            <div className="form-group">
              <label>Name</label>
              <input 
                type="text" 
                value={userProfile.name}
                onChange={(e) => setUserProfile({...userProfile, name: e.target.value})}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input 
                type="email" 
                value={userProfile.email}
                onChange={(e) => setUserProfile({...userProfile, email: e.target.value})}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input 
                type="tel" 
                value={userProfile.phone}
                onChange={(e) => setUserProfile({...userProfile, phone: e.target.value})}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Default License Plate</label>
              <input 
                type="text" 
                value={userProfile.licensePlate}
                onChange={(e) => setUserProfile({...userProfile, licensePlate: e.target.value.toUpperCase()})}
                className="form-input"
              />
            </div>
            <button className="settings-button">Save Profile</button>
          </div>
        </div>
        
        <div className="settings-card">
          <h3>Display Settings</h3>
          <div className="settings-form">
            <div className="form-group">
              <label>Theme</label>
              <div className="toggle-switch">
                <span>Light</span>
                <label className="switch">
                  <input 
                    type="checkbox" 
                    checked={darkMode}
                    onChange={() => setDarkMode(!darkMode)}
                  />
                  <span className="slider round"></span>
                </label>
                <span>Dark</span>
              </div>
            </div>
            <div className="form-group">
              <label>Default View</label>
              <select className="form-select">
                <option value="dashboard">Dashboard</option>
                <option value="camera">Camera</option>
                <option value="analytics">Analytics</option>
              </select>
            </div>
            <div className="form-group">
              <label>Slot View</label>
              <div className="toggle-switch">
                <span>Grid</span>
                <label className="switch">
                  <input 
                    type="checkbox" 
                    checked={slotView === 'map'}
                    onChange={() => setSlotView(slotView === 'grid' ? 'map' : 'grid')}
                  />
                  <span className="slider round"></span>
                </label>
                <span>Map</span>
              </div>
            </div>
            <button className="settings-button">Save Settings</button>
          </div>
        </div>
        
        <div className="settings-card">
          <h3>System Configuration</h3>
          <div className="settings-form">
            <div className="form-group">
              <label>Hourly Rate ($)</label>
              <input type="number" defaultValue="5" className="form-input" />
            </div>
            <div className="form-group">
              <label>Cooldown Period (minutes)</label>
              <input type="number" defaultValue="2" className="form-input" />
            </div>
            <div className="form-group">
              <label>Default Reservation Duration (hours)</label>
              <input type="number" defaultValue="1" className="form-input" />
            </div>
            <div className="form-group">
              <label>Camera Detection Interval (ms)</label>
              <input type="number" defaultValue="1500" className="form-input" />
            </div>
            <button className="settings-button">Save Configuration</button>
          </div>
        </div>
        
        <div className="settings-card">
          <h3>Notification Settings</h3>
          <div className="settings-form">
            <div className="form-group">
              <label>Email Notifications</label>
              <div className="toggle-switch">
                <span>Off</span>
                <label className="switch">
                  <input type="checkbox" defaultChecked />
                  <span className="slider round"></span>
                </label>
                <span>On</span>
              </div>
            </div>
            <div className="form-group">
              <label>SMS Notifications</label>
              <div className="toggle-switch">
                <span>Off</span>
                <label className="switch">
                  <input type="checkbox" />
                  <span className="slider round"></span>
                </label>
                <span>On</span>
              </div>
            </div>
            <div className="form-group">
              <label>Push Notifications</label>
              <div className="toggle-switch">
                <span>Off</span>
                <label className="switch">
                  <input type="checkbox" defaultChecked />
                  <span className="slider round"></span>
                </label>
                <span>On</span>
              </div>
            </div>
            <button className="settings-button">Save Notification Settings</button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render main content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'camera':
        return renderCamera();
      case 'analytics':
        return renderAnalytics();
      case 'settings':
        return renderSettings();
      default:
        return renderDashboard();
    }
  };

  // Loading state
  if (loadingData && !slots.length) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading Smart Parking System...</div>
      </div>
    );
  }

  // Error state
  if (error && !slots.length) {
    return (
      <div className="error-container">
        <FaExclamationCircle size={48} className="error-icon" />
        <div className="error-text">{error}</div>
        <button 
          className="retry-button"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`app-container ${darkMode ? 'dark-mode' : ''}`}>
      <header className="app-header">
        <div className="logo">
          <FaCar size={28} />
          <h1>Smart Parking System</h1>
        </div>
        
        <nav className="main-nav">
          <button 
            className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <FaHome size={18} />
            Dashboard
          </button>
          <button 
            className={`nav-btn ${activeTab === 'camera' ? 'active' : ''}`}
            onClick={() => setActiveTab('camera')}
          >
            <FaCamera size={18} />
            Camera
          </button>
          <button 
            className={`nav-btn ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <FaChartBar size={18} />
            Analytics
          </button>
          <button 
            className={`nav-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <FaCog size={18} />
            Settings
          </button>
        </nav>
      </header>
      
      <main className="app-main">
        {renderContent()}
      </main>
      
      {/* Receipt Modal */}
      {showReceipt && (
        <div className="modal-overlay">
          <div className="modal-container receipt-modal">
            <div className="modal-header">
              <h3>Parking Receipt</h3>
              <button 
                className="close-btn"
                onClick={() => setShowReceipt(false)}
              >
                <FaTimes size={24} />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="receipt" ref={receiptRef}>
                <div className="receipt-header">
                  <div className="receipt-logo">Smart Parking System</div>
                  <div className="receipt-title">Parking Receipt</div>
                </div>
                
                <div className="receipt-details">
                  <div className="receipt-row">
                    <div className="receipt-label">Vehicle:</div>
                    <div>{receiptData?.vehicleNumber}</div>
                  </div>
                  <div className="receipt-row">
                    <div className="receipt-label">Slot:</div>
                    <div>{receiptData?.slotNumber}</div>
                  </div>
                  <div className="receipt-row">
                    <div className="receipt-label">Entry Time:</div>
                    <div>{formatDateTime(receiptData?.entryTime)}</div>
                  </div>
                  <div className="receipt-row">
                    <div className="receipt-label">Exit Time:</div>
                    <div>{formatDateTime(receiptData?.exitTime)}</div>
                  </div>
                  <div className="receipt-row">
                    <div className="receipt-label">Duration:</div>
                    <div>{formatDuration(receiptData?.entryTime, receiptData?.exitTime)}</div>
                  </div>
                  <div className="receipt-row">
                    <div className="receipt-label">Fee:</div>
                    <div>${receiptData?.fee?.toFixed(2)}</div>
                  </div>
                  <div className="receipt-row">
                    <div className="receipt-label">Payment Method:</div>
                    <div>{receiptData?.paymentMethod || 'Credit Card'}</div>
                  </div>
                </div>
                
                <div className="receipt-qr">
                  <QRCodeSVG 
                    value={JSON.stringify({
                      vehicleNumber: receiptData?.vehicleNumber,
                      slotNumber: receiptData?.slotNumber,
                      entryTime: receiptData?.entryTime,
                      exitTime: receiptData?.exitTime,
                      fee: receiptData?.fee
                    })}
                    size={120}
                  />
                </div>
                
                <div className="receipt-footer">
                  <div>Thank you for using our parking service!</div>
                  <div>Date: {new Date().toLocaleDateString()}</div>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="modal-btn secondary"
                onClick={printReceipt}
              >
                <a size={18} />
                Print
              </button>
              <button 
                className="modal-btn primary"
                onClick={emailReceipt}
              >
                <FaEnvelope size={18} />
                Email Receipt
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reservation Modal */}
      {showReservationModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Reserve Parking Slot</h3>
              <button 
                className="close-btn"
                onClick={() => setShowReservationModal(false)}
              >
                <FaTimes size={24} />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="reservation-form">
                <div className="form-group">
                  <label>Slot Number</label>
                  <select
                    value={reserveSlot}
                    onChange={(e) => setReserveSlot(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select a slot</option>
                    {slots
                      .filter(slot => !slot.isOccupied && !slot.isReserved)
                      .map(slot => (
                        <option key={slot.slotNumber} value={slot.slotNumber}>
                          {slot.slotNumber}
                        </option>
                      ))
                    }
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={reserveEmail}
                    onChange={(e) => setReserveEmail(e.target.value)}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>Phone (optional)</label>
                  <input
                    type="tel"
                    placeholder="Your phone number"
                    value={reservePhone}
                    onChange={(e) => setReservePhone(e.target.value)}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>Reservation Date</label>
                  <DatePicker
                    selected={reserveDate}
                    onChange={(date) => setReserveDate(date)}
                    showTimeSelect
                    dateFormat="MMMM d, yyyy h:mm aa"
                    minDate={new Date()}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>Duration (hours)</label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={reserveDuration}
                    onChange={(e) => setReserveDuration(parseInt(e.target.value))}
                    className="form-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="modal-btn secondary"
                onClick={() => setShowReservationModal(false)}
              >
                Cancel
              </button>
              <button 
                className="modal-btn primary"
                onClick={handleReserveSlot}
                disabled={!reserveSlot || !reserveEmail}
              >
                Reserve Slot
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay">
          <div className="modal-container payment-modal">
            <div className="modal-header">
              <h3>Payment Processing</h3>
              <button 
                className="close-btn"
                onClick={() => setShowPaymentModal(false)}
              >
                <FaTimes size={24} />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="payment-details">
                <div className="payment-amount">
                  <div className="amount-label">Total Amount:</div>
                  <div className="amount-value">${paymentAmount.toFixed(2)}</div>
                </div>
                
                <div className="payment-method-selector">
                  <div className="method-label">Select Payment Method:</div>
                  <div className="method-options">
                    {PAYMENT_METHODS.map(method => (
                      <div 
                        key={method}
                        className={`method-option ${paymentMethod === method ? 'selected' : ''}`}
                        onClick={() => setPaymentMethod(method)}
                      >
                        {method}
                      </div>
                    ))}
                  </div>
                </div>
                
                {paymentMethod === 'Credit Card' && (
                  <div className="card-details">
                    <div className="form-group">
                      <label>Card Number</label>
                      <input
                        type="text"
                        placeholder="1234 5678 9012 3456"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Expiry Date</label>
                        <input
                          type="text"
                          placeholder="MM/YY"
                          value={cardExpiry}
                          onChange={(e) => setCardExpiry(e.target.value)}
                          className="form-input"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>CVC</label>
                        <input
                          type="text"
                          placeholder="123"
                          value={cardCVC}
                          onChange={(e) => setCardCVC(e.target.value)}
                          className="form-input"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="payment-status">
                  <div className="status-icon">
                    <div className="processing-spinner"></div>
                  </div>
                  <div className="status-text">Processing payment...</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* QR Code Modal */}
      {showQRModal && qrData && (
        <div className="modal-overlay">
          <div className="modal-container qr-modal">
            <div className="modal-header">
              <h3>{qrData.type === 'reservation' ? 'Reservation QR Code' : 'Parking QR Code'}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowQRModal(false)}
              >
                <FaTimes size={24} />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="qr-container">
                <div className="qr-code">
                  <QRCodeSVG 
                    value={JSON.stringify(qrData)}
                    size={200}
                  />
                </div>
                
                <div className="qr-info">
                  <p>
                    {qrData.type === 'reservation' 
                      ? 'Use this QR code to claim your reserved parking slot.' 
                      : 'Use this QR code for contactless entry and exit.'}
                  </p>
                  
                  {qrData.type === 'reservation' && (
                    <div className="reservation-details">
                      <div className="detail-row">
                        <div className="detail-label">Slot:</div>
                        <div className="detail-value">{qrData.slotNumber}</div>
                      </div>
                      <div className="detail-row">
                        <div className="detail-label">Date:</div>
                        <div className="detail-value">{formatDateTime(qrData.reservationDate)}</div>
                      </div>
                      <div className="detail-row">
                        <div className="detail-label">Duration:</div>
                        <div className="detail-value">{qrData.duration} hour(s)</div>
                      </div>
                    </div>
                  )}
                  
                  {qrData.type === 'parking_session' && (
                    <div className="parking-details">
                      <div className="detail-row">
                        <div className="detail-label">Vehicle:</div>
                        <div className="detail-value">{qrData.vehicleNumber}</div>
                      </div>
                      <div className="detail-row">
                        <div className="detail-label">Slot:</div>
                        <div className="detail-value">{qrData.slotNumber}</div>
                      </div>
                      <div className="detail-row">
                        <div className="detail-label">Entry Time:</div>
                        <div className="detail-value">{formatDateTime(qrData.entryTime)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="modal-btn secondary"
                onClick={() => {
                  // This would download the QR code in a real implementation
                  addNotification('success', 'QR Code Saved', 'QR code has been saved to your device');
                }}
              >
                <a size={18} />
                Save QR
              </button>
              <button 
                className="modal-btn primary"
                onClick={() => {
                  // This would email the QR code in a real implementation
                  addNotification('success', 'QR Code Sent', 'QR code has been sent to your email');
                  setShowQRModal(false);
                }}
              >
                <FaEnvelope size={18} />
                Email QR
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast notifications */}
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default App;
